import { HttpClient } from '@euler/lib/request';
import { SSEConnection } from '@euler/lib/request/SSEClient';
import { createAuthorizedSSEConnection } from '@euler/lib/request/authorizedSSE';
import { TokenService } from '@euler/lib/services';
import {
  CoachingAttemptListRequest,
  CoachingAttemptListResult,
  CoachingAttemptView,
  CoachingCaseBrowseRequest,
  CoachingCaseBrowseResult,
  CoachingCaseDetail,
  CoachingCustomerGender,
  CoachingLibraryCatalogue,
  CoachingRelatedCasesResult,
  CoachingSetupStartResult,
  CoachingSuggestion,
  CoachingSuggestionsResult,
} from '@euler/model/coaching';
import {
  CoachingAnchor,
  CoachingAttemptAgainRequest,
  CoachingPracticeAudio,
  CoachingPracticeEnd,
  CoachingPracticeHint,
  CoachingPracticeOpened,
  CoachingPracticeReportView,
  CoachingPracticeTurnCallbacks,
  CoachingPracticeTurnRequest,
  CoachingPracticeVoiceAudio,
  CoachingReplayOpened,
  CoachingReplaySummary,
} from '@euler/model/coaching-practice';
import {
  CoachingLeaderboardView,
  CoachingProfileView,
  CoachingStreakView,
} from '@euler/model/coaching-progress';
import { TokenRefresher } from './ai-assistant.service';

/**
 * Sales coaching. `api` is euler, which serves the case library and the
 * trainee's attempts; `aiApi` is ai-gateway, where Start may need a model
 * call to write up the trainee's own story. Path ids are encoded here: the
 * client inserts them as-is, and a case id can come from a deep link.
 */
export class CoachingService {
  constructor(
    private readonly api: HttpClient,
    private readonly aiApi: HttpClient,
    private readonly tokenService: TokenService,
    /** ai-gateway, which streams practice turns. */
    private readonly endpoint: string,
    private readonly refreshToken?: TokenRefresher,
  ) {}

  async getCatalogue(): Promise<CoachingLibraryCatalogue> {
    return await this.api.get().url('/coaching/library/catalogue').future();
  }

  async getSuggestions(): Promise<CoachingSuggestion[]> {
    const result: CoachingSuggestionsResult = await this.api
      .get()
      .url('/coaching/library/suggestions')
      .future();
    return result.suggestions;
  }

  async searchCases(
    request: CoachingCaseBrowseRequest,
  ): Promise<CoachingCaseBrowseResult> {
    return await this.api
      .post()
      .url('/coaching/library/cases/search')
      .data({ request })
      .future();
  }

  async getRelatedCases(caseId: string): Promise<CoachingRelatedCasesResult> {
    return await this.api
      .get()
      .url('/coaching/library/cases/:caseId/related', {
        caseId: encodeURIComponent(caseId),
      })
      .future();
  }

  async getCase(caseId: string): Promise<CoachingCaseDetail> {
    return await this.api
      .get()
      .url('/coaching/library/cases/:caseId', {
        caseId: encodeURIComponent(caseId),
      })
      .future();
  }

  async listAttempts(
    request: CoachingAttemptListRequest,
  ): Promise<CoachingAttemptView[]> {
    const result: CoachingAttemptListResult = await this.api
      .get()
      .url('/coaching/attempts', {
        beforeId: request.beforeId,
        limit: request.limit,
      })
      .future();
    return result.attempts;
  }

  /** Plays an attempt again on the same customer; one per request key. */
  async replayAttempt(
    attemptId: string,
    requestKey: string,
  ): Promise<CoachingAttemptView> {
    const result: CoachingSetupStartResult = await this.api
      .post()
      .url('/coaching/attempts/:attemptId/replay', {
        attemptId: encodeURIComponent(attemptId),
      })
      .data({ requestKey })
      .future();
    return result.attempt;
  }

  /**
   * The summary card's Start; repeated presses return the same attempt, and
   * a setup changed since the card is refused (`coaching_setup_changed`).
   */
  async startSetup(
    setupId: string,
    settledKey: string,
    /** The customer's gender picked on the card; null keeps the case's. */
    customerGender: CoachingCustomerGender | null,
  ): Promise<CoachingAttemptView> {
    const result: CoachingSetupStartResult = await this.aiApi
      .post()
      .url('/coaching/setups/:setupId/start', {
        setupId: encodeURIComponent(setupId),
      })
      .data({ settledKey, customerGender: customerGender ?? undefined })
      .future();
    return result.attempt;
  }

  /** 我的练习's profile: eight areas, stars per case and level, milestones. */
  async getProfile(): Promise<CoachingProfileView> {
    return await this.api.get().url('/coaching/progress/profile').future();
  }

  async getStreak(): Promise<CoachingStreakView> {
    return await this.api.get().url('/coaching/progress/streak').future();
  }

  async setWeeklyGoal(weeklyGoal: number): Promise<CoachingStreakView> {
    return await this.api
      .post()
      .url('/coaching/progress/goal')
      .data({ weeklyGoal })
      .future();
  }

  /** 本周练习榜 of the trainee's store. */
  async getLeaderboard(): Promise<CoachingLeaderboardView> {
    return await this.api.get().url('/coaching/progress/leaderboard').future();
  }

  /** 再练一次 / 升一级 from a report; one attempt per request key. */
  async practiceAgain(
    attemptId: string,
    request: CoachingAttemptAgainRequest,
  ): Promise<CoachingAttemptView> {
    const result: CoachingSetupStartResult = await this.api
      .post()
      .url('/coaching/attempts/:attemptId/again', {
        attemptId: encodeURIComponent(attemptId),
      })
      .data(request)
      .future();
    return result.attempt;
  }

  /**
   * Opens (or resumes) a practice scene. `speak` false when the trainee
   * muted the customer: nothing is voiced or billed.
   */
  async openPractice(
    attemptId: string,
    speak: boolean,
  ): Promise<CoachingPracticeOpened> {
    return await this.aiApi
      .post()
      .url('/coaching/attempts/:attemptId/practice', {
        attemptId: encodeURIComponent(attemptId),
      })
      .data({ speak })
      .future();
  }

  /** One trainee line; the customer's reply and the coach stream back. */
  practiceTurn(
    attemptId: string,
    request: CoachingPracticeTurnRequest,
    callbacks: CoachingPracticeTurnCallbacks,
  ): SSEConnection {
    return this.stream(
      `/api/ai/coaching/attempts/${encodeURIComponent(
        attemptId,
      )}/practice/turns`,
      request,
      callbacks,
    );
  }

  /** One trainee line of a moment replay, streamed like a practice turn. */
  replayTurn(
    replayId: string,
    request: CoachingPracticeTurnRequest,
    callbacks: CoachingPracticeTurnCallbacks,
  ): SSEConnection {
    return this.stream(
      `/api/ai/coaching/practice/replays/${encodeURIComponent(replayId)}/turns`,
      request,
      callbacks,
    );
  }

  /** A hint for the next line, asked for once the trainee goes quiet. */
  async practiceHint(attemptId: string): Promise<CoachingPracticeHint> {
    return await this.aiApi
      .post()
      .url('/coaching/attempts/:attemptId/practice/hint', {
        attemptId: encodeURIComponent(attemptId),
      })
      .future();
  }

  async stopPractice(attemptId: string): Promise<CoachingPracticeEnd> {
    return await this.aiApi
      .post()
      .url('/coaching/attempts/:attemptId/practice/stop', {
        attemptId: encodeURIComponent(attemptId),
      })
      .future();
  }

  /** The report; the first ask writes it, which takes a few seconds. */
  async practiceReport(attemptId: string): Promise<CoachingPracticeReportView> {
    return await this.aiApi
      .post()
      .url('/coaching/attempts/:attemptId/practice/report', {
        attemptId: encodeURIComponent(attemptId),
      })
      .future();
  }

  /** A line's stored voice, to play it back from the transcript. */
  async practiceVoice(voiceId: string): Promise<CoachingPracticeVoiceAudio> {
    return await this.aiApi
      .get()
      .url('/coaching/practice/voices/:voiceId', {
        voiceId: encodeURIComponent(voiceId),
      })
      .future();
  }

  /** A customer line of the transcript, spoken again. */
  async voicePracticeLine(
    attemptId: string,
    turn: number,
  ): Promise<CoachingPracticeAudio[]> {
    return await this.aiApi
      .post()
      .url('/coaching/attempts/:attemptId/practice/lines/:turn/voice', {
        attemptId: encodeURIComponent(attemptId),
        turn: String(turn),
      })
      .future();
  }

  /** 从这里重试: reopens the scene at the moment. */
  async openReplay(
    attemptId: string,
    turn: number,
    speak: boolean,
  ): Promise<CoachingReplayOpened> {
    return await this.aiApi
      .post()
      .url('/coaching/attempts/:attemptId/practice/replays', {
        attemptId: encodeURIComponent(attemptId),
      })
      .data({ turn, speak })
      .future();
  }

  async listReplays(attemptId: string): Promise<CoachingReplaySummary[]> {
    return await this.aiApi
      .post()
      .url('/coaching/attempts/:attemptId/practice/replays/list', {
        attemptId: encodeURIComponent(attemptId),
      })
      .future();
  }

  private stream(
    path: string,
    body: unknown,
    callbacks: CoachingPracticeTurnCallbacks,
  ): SSEConnection {
    return createAuthorizedSSEConnection({
      endpoint: this.endpoint,
      path,
      body,
      tokenService: this.tokenService,
      refreshToken: this.refreshToken,
      onEvent: event => {
        const data = JSON.parse(event.data);
        switch (event.event) {
          case 'turn':
            callbacks.onTurn?.(data);
            break;
          case 'customer_text':
            callbacks.onCustomerText?.(data);
            break;
          case 'customer_audio':
            callbacks.onCustomerAudio?.(data);
            break;
          case 'customer_done':
            callbacks.onCustomerDone?.(data);
            break;
          case 'mood':
            callbacks.onMood?.(data);
            break;
          case 'evidence':
            callbacks.onEvidence?.(data);
            break;
          case 'reaction':
            callbacks.onReaction?.(data);
            break;
          case 'ended':
            callbacks.onEnded?.(data);
            break;
          case 'verdict':
            callbacks.onVerdict?.(data as { verdict: CoachingAnchor });
            break;
          case 'done':
            callbacks.onDone?.(data);
            break;
          case 'error':
            callbacks.onError?.(data.message ?? '', data.subCode ?? data.code);
            break;
        }
      },
      onError: message => callbacks.onError?.(message),
    });
  }
}
